/**
 * "…which areas?" — the places the matches happened in.
 *
 * ─── THE TREE IS NOT DECORATION ───
 * A camera's group list holds every ANCESTOR, so one camera in Reception credits Reception, the
 * Office that contains it and the site above that. Group rows therefore OVERLAP and do not sum to
 * the total. Rendered as a flat ranked list that reads as a bug — a hundred detections split
 * across rows adding up to three hundred — so when the server says `overlapping`, the rows are
 * nested and each child is visibly inside its parent. Nesting turns the double count from an
 * error into the answer's actual shape: yes, it happened in Reception, and Reception is in the
 * Office.
 *
 * Camera and zone groupings are disjoint, so they stay a flat ranked list.
 *
 * ─── A ROW IS A WAY BACK INTO THE QUERY ───
 * Clicking a place narrows the search to it. "Which areas" and then "show me the ones in the
 * server room" is the actual investigative move, and making the operator go find that group in
 * the Groups dropdown to do it would waste the answer they just got.
 */
import type { ReactElement } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { Group, SearchPlaceBy, SearchPlaceRow, SearchResult } from '../../../api/contracts';
import {
  AnswerEmpty, AnswerShell, Caveat, Figure, fmtTime,
  makeParentOf, type PlaceNamer,
} from './common';

export interface WhereAnswerProps {
  result: SearchResult;
  namePlace: PlaceNamer;
  groups: Group[];
  /** Narrow the query to this place. Undefined leaves rows unclickable. */
  onPickPlace?: (place: string, placeBy: SearchPlaceBy) => void;
}

/** A place row plus the children nested under it. Only ever built for the group roll-up. */
interface Node { row: SearchPlaceRow; children: Node[] }

/**
 * Group rows → a forest, using the parent links the page already has from its group list.
 *
 * A row whose parent is not itself in the result becomes a root: that happens whenever the parent
 * area saw nothing the child did not, and hanging the child off a row that is not there would
 * lose it entirely.
 */
function nest(rows: SearchPlaceRow[], parentOf: (id: string) => string | null): Node[] {
  const nodes = new Map(rows.map((row) => [row.place, { row, children: [] as Node[] }]));
  const roots: Node[] = [];

  for (const node of nodes.values()) {
    const parent = parentOf(node.row.place);
    const parentNode = parent ? nodes.get(parent) : null;
    if (parentNode && parentNode !== node) parentNode.children.push(node);
    else roots.push(node);
  }

  const byEvents = (a: Node, b: Node) => b.row.events - a.row.events;
  const sortDeep = (list: Node[]) => {
    list.sort(byEvents);
    for (const n of list) sortDeep(n.children);
  };
  sortDeep(roots);
  return roots;
}

function PlaceRow({
  row, placeBy, namePlace, depth, subjectsKnown, maxEvents, onPick,
}: {
  row: SearchPlaceRow;
  placeBy: SearchPlaceBy;
  namePlace: PlaceNamer;
  depth: number;
  subjectsKnown: boolean;
  maxEvents: number;
  onPick?: () => void;
}) {
  const name = row.unassigned ? row.place : namePlace(row.place, placeBy);
  // A bar behind the row rather than beside it: the ranking is already the order, and the bar is
  // there to show how far ahead the leader is without spending a column on a chart.
  const share = maxEvents > 0 ? Math.max(row.events / maxEvents, 0.02) : 0;

  return (
    <button
      type="button"
      disabled={!onPick}
      onClick={onPick}
      className={[
        'relative flex w-full items-center gap-3 overflow-hidden rounded px-2 py-1.5 text-left',
        onPick ? 'hover:bg-muted' : 'cursor-default',
      ].join(' ')}
      style={{ paddingLeft: `${0.5 + depth * 1.1}rem` }}
      title={onPick ? `Narrow the search to ${name}` : undefined}
    >
      <span
        className="pointer-events-none absolute inset-y-0 left-0 bg-brand-light"
        style={{ width: `${share * 100}%` }}
        aria-hidden
      />
      <span className={`relative min-w-0 flex-1 truncate text-xs ${row.unassigned ? 'italic text-muted-foreground' : 'text-foreground'}`}>
        {name}
        {typeof row.cameras === 'number' && row.cameras > 1 && (
          <span className="ml-1.5 text-[10px] text-muted-foreground">
            {row.cameras} cameras
          </span>
        )}
      </span>
      <span className="relative shrink-0 tabular-nums text-xs text-foreground">{row.events}</span>
      <span className="relative w-24 shrink-0 text-right text-[11px] text-muted-foreground">
        {/* A dash, never a blank, when the figure was not computed — a blank reads as zero. */}
        {subjectsKnown ? `${row.subjects} ${row.subjects === 1 ? 'person' : 'people'}` : '— people'}
      </span>
      <span className="relative w-28 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
        {fmtTime(row.first_seen)} → {fmtTime(row.last_seen)}
      </span>
      {onPick && <ChevronRightIcon className="relative h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
    </button>
  );
}

export default function WhereAnswer({ result, namePlace, groups, onPickPlace }: WhereAnswerProps) {
  const rows = (result.rows || []) as SearchPlaceRow[];
  const placeBy = (result.place_by || 'group') as SearchPlaceBy;

  if (!rows.length) {
    return <AnswerEmpty cameraScope={result.camera_scope} hint="Nothing matched anywhere in scope." />;
  }

  const subjectsKnown = !result.subjects_omitted;
  const maxEvents = Math.max(...rows.map((r) => r.events), 1);
  const busiest = rows.reduce((a, b) => (b.events > a.events ? b : a));
  const busiestName = busiest.unassigned ? busiest.place : namePlace(busiest.place, placeBy);

  // Only the group roll-up nests. The others are disjoint, so a tree would imply a containment
  // that is not there.
  const tree = result.overlapping ? nest(rows, makeParentOf(groups)) : null;

  const kind = placeBy === 'group' ? 'area' : placeBy === 'camera' ? 'camera' : 'zone';

  const render = (nodes: Node[], depth = 0): ReactElement[] => nodes.flatMap((node) => [
    <PlaceRow
      key={node.row.place}
      row={node.row}
      placeBy={placeBy}
      namePlace={namePlace}
      depth={depth}
      subjectsKnown={subjectsKnown}
      maxEvents={maxEvents}
      onPick={onPickPlace && !node.row.unassigned
        ? () => onPickPlace(node.row.place, placeBy)
        : undefined}
    />,
    ...render(node.children, depth + 1),
  ]);

  return (
    <AnswerShell
      headline={`Busiest ${kind}: ${busiestName}`}
      sub={`${rows.length} ${rows.length === 1 ? kind : `${kind}s`} with a match${onPickPlace ? ' — click one to narrow the search to it' : ''}`}
      figures={(
        <>
          <Figure value={rows.length} label={`${kind}s with a match`} />
          <Figure value={busiest.events} label={`detections in ${busiestName}`} />
          {typeof result.camera_scope === 'number' && (
            <Figure value={result.camera_scope} label={result.camera_scope === 1 ? 'camera searched' : 'cameras searched'} />
          )}
        </>
      )}
      caveats={(
        <>
          {result.overlapping && (
            <Caveat>
              Areas nest, so these rows do not add up to the total — a detection in Reception also
              counts for the area that contains it. Indentation shows what is inside what.
            </Caveat>
          )}
          {result.group_map_cold && (
            <Caveat>
              The camera-to-area map has not loaded on this node, so everything is reporting as
              ungrouped. Try grouping by camera instead.
            </Caveat>
          )}
          {result.subjects_omitted && (
            <Caveat>
              Counting distinct people per area was too expensive for this window, so the people
              column is not shown. Narrow the window or add a filter and it returns.
            </Caveat>
          )}
          {placeBy === 'zone' && rows.some((r) => r.unassigned) && (
            <Caveat>
              “(no zone)” is not an error — those cameras have no regions of interest configured,
              so their detections belong to no zone.
            </Caveat>
          )}
        </>
      )}
    >
      <div className="rounded-md border border-border bg-card p-1.5">
        <div className="flex items-center gap-3 border-b border-border px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span className="min-w-0 flex-1">{kind}</span>
          <span className="shrink-0">Events</span>
          <span className="w-24 shrink-0 text-right">People</span>
          <span className="w-28 shrink-0 text-right">First → last</span>
          {onPickPlace && <span className="w-3.5 shrink-0" />}
        </div>
        {tree ? render(tree) : rows.map((row) => (
          <PlaceRow
            key={row.place}
            row={row}
            placeBy={placeBy}
            namePlace={namePlace}
            depth={0}
            subjectsKnown={subjectsKnown}
            maxEvents={maxEvents}
            onPick={onPickPlace && !row.unassigned ? () => onPickPlace(row.place, placeBy) : undefined}
          />
        ))}
      </div>
    </AnswerShell>
  );
}
